// useBoothRecording — mic / recorder control of the Recording Booth (split
// out of RecordingStudio.tsx): up-front mic permission + input-device list
// (FB#24/FB#26), preroll countdown (FB#25/FB#27), record / stop with
// head-trim, and the mid-recording interruption path (Audit M24). Builds on
// useRecording; the booth-wide state machine (`RecState`) lives here because
// recording drives most of its transitions — Generate (useStsGenerate) and
// the overlay's reset/dismiss paths use the returned `setState`.

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
} from 'react';
import {
  useRecording,
  trimAudioHead,
  type AudioInputDevice,
  type PrerollHandle,
} from '@/hooks/useRecording';
import {
  MIC_DISCONNECTED_MESSAGE,
  type RecordingInterruption,
} from '@/hooks/recorderControl';
import { stopAllPlayers } from '@/lib/audioPlayback';
import type { LoadedSource, PermState, RecState } from './boothTypes';

interface UseBoothRecordingArgs {
  open: boolean;
  toolId: string | null;
  currentAssetIdx: number;
  /** Store action: claims the next recording-session number of the asset. */
  beginStsRecordingSession(assetIdx: number): number;
  hasVoice: boolean;
  /** Session take limit reached — no new recording / upload. */
  atLimit: boolean;
  /** The booth's inline reference <video> (started at "go", parked on Stop). */
  videoElRef: MutableRefObject<HTMLVideoElement | null>;
  toast: { show(message: string, ms?: number): void };
  setPlayingTakeId: Dispatch<SetStateAction<string | null>>;
  setLoadedSource: Dispatch<SetStateAction<LoadedSource | null>>;
  setSessionNum: Dispatch<SetStateAction<number | null>>;
}

export function useBoothRecording({
  open,
  toolId,
  currentAssetIdx,
  beginStsRecordingSession,
  hasVoice,
  atLimit,
  videoElRef,
  toast,
  setPlayingTakeId,
  setLoadedSource,
  setSessionNum,
}: UseBoothRecordingArgs) {
  // Mikro mitten in der Aufnahme getrennt (Headset ab) oder Recorder-Fehler
  // (Audit M24). Läuft die Aufnahme, wird sie wie per Stop abgeschlossen —
  // das bis dahin Aufgenommene bleibt als Quelle geladen. Im Countdown wird
  // abgebrochen. Refs, weil der Handler aus dem Recorder-Event heraus
  // aufgerufen wird und den aktuellen Stand braucht.
  const stateRef = useRef<RecState>({ kind: 'idle' });
  const stopRecordingRef = useRef<() => Promise<void>>(async () => {});
  const interruptedRef = useRef(false);
  const handleInterrupted = useCallback((reason: RecordingInterruption) => {
    interruptedRef.current = true;
    const message =
      reason === 'device-lost'
        ? MIC_DISCONNECTED_MESSAGE
        : 'The recording stopped unexpectedly. Please record again.';
    const kind = stateRef.current.kind;
    if (kind === 'recording') {
      toast.show(message, 5000);
      void stopRecordingRef.current();
    } else if (kind === 'preroll') {
      recordingRef.current.cancel();
      setState({ kind: 'error', message });
    }
  }, [toast]);
  const recording = useRecording({ onInterrupted: handleInterrupted });

  const [state, setState] = useState<RecState>({ kind: 'idle' });
  stateRef.current = state;
  // Preroll countdown overlay (3 → 2 → 1 → go).
  const [countdown, setCountdown] = useState<number | null>(null);
  // FB#24/FB#26: pre-prompt mic permission on overlay OPEN, then expose the
  // enumerated audio inputs in a dropdown above the Record button.
  const [permState, setPermState] = useState<PermState>({ kind: 'pending' });
  const [inputDevices, setInputDevices] = useState<AudioInputDevice[]>([]);
  // `selectedDeviceId === ''` means "system default" (don't pin a deviceId).
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');
  // FB#27: wall-clock "go" moment of the current preroll. Captured in
  // startRecording() and read in stopRecording() to compute how much of the
  // recording's head to trim (recorder starts mid-countdown, see below).
  const prerollEndsAtRef = useRef<number | null>(null);

  // Reset the hook-owned part of the overlay state whenever the overlay
  // opens afresh (the rest is reset by RecordingStudio's open-effect right
  // before this one — same deps, same commit).
  useEffect(() => {
    if (!open) return;
    setState({ kind: 'idle' });
    setCountdown(null);
    // Don't reset selectedDeviceId — preserving the per-session picker choice
    // across overlay close/reopen is one of the FB#24 requirements.
    setPermState({ kind: 'pending' });
    setInputDevices([]);
  }, [open, beginStsRecordingSession, currentAssetIdx]);

  // Cancel any in-flight recorder when the overlay closes abruptly. Also
  // release the held mic stream so the browser's mic indicator goes away
  // when the overlay was opened with up-front permission but never recorded.
  // Depend on `open` only — `recording` functions are useCallback-stable
  // inside `useRecording`, but the wrapper object is a fresh literal each
  // render and would false-trigger this effect every time.
  const recordingRef = useRef(recording);
  recordingRef.current = recording;
  useEffect(() => {
    if (!open) {
      recordingRef.current.cancel();
      recordingRef.current.release();
      return;
    }
    // Unmount while the overlay is still open (browser back mid-recording,
    // route change): without this cleanup neither recorder.stop() nor
    // stream.getTracks().stop() ever runs — the mic stays hot with no UI
    // and the MediaRecorder keeps collecting chunks. Reading the ref in the
    // cleanup is intentional: we want whatever recorder/stream is CURRENT
    // at unmount time, not the one from this render.
    return () => {
      recordingRef.current.cancel();
      recordingRef.current.release();
    };
  }, [open]);

  // FB#26: pre-prompt mic permission as soon as the overlay opens. Success
  // unlocks device labels (FB#24); failure shows an inline retry message in
  // place of the device picker.
  //
  // We use a token (`reqId`) so an unmount or rapid open/close doesn't apply
  // a stale resolve to a newer overlay run.
  const permReqRef = useRef(0);
  const requestMicPermission = useCallback(async () => {
    const reqId = ++permReqRef.current;
    setPermState({ kind: 'pending' });
    try {
      // Acquire WITHOUT a deviceId — labels populate even if the user later
      // picks a different device. The acquired stream is held on the
      // recording-hook's ref and reused by `startRecording()` if no other
      // device is selected.
      await recordingRef.current.acquire(null);
      const list = await recordingRef.current.enumerateInputs();
      if (reqId !== permReqRef.current) return; // overlay re-opened or closed
      setInputDevices(list);
      setPermState({ kind: 'granted' });
    } catch (e: unknown) {
      if (reqId !== permReqRef.current) return;
      const msg =
        e instanceof Error
          ? e.message
          : 'Microphone access was denied.';
      setPermState({ kind: 'denied', message: msg });
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    void requestMicPermission();
    // Bump request id on close so any pending then() drops its result.
    return () => {
      permReqRef.current += 1;
    };
  }, [open, requestMicPermission]);

  // Refresh the device list when the system reports a hotplug/unplug. Only
  // active while the overlay is open and permission is granted (otherwise
  // labels would be empty + the dropdown wouldn't be visible anyway).
  useEffect(() => {
    if (!open || permState.kind !== 'granted') return;
    if (!navigator.mediaDevices?.addEventListener) return;
    const onChange = () => {
      void recordingRef.current.enumerateInputs().then((list) => {
        setInputDevices(list);
      });
    };
    navigator.mediaDevices.addEventListener('devicechange', onChange);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', onChange);
    };
  }, [open, permState.kind]);

  // If the user picks a device that's no longer present (e.g. unplugged
  // between renders), fall back to "system default" rather than failing
  // `getUserMedia` with `OverconstrainedError` later.
  useEffect(() => {
    if (!selectedDeviceId) return;
    if (inputDevices.length === 0) return;
    if (!inputDevices.some((d) => d.deviceId === selectedDeviceId)) {
      setSelectedDeviceId('');
    }
  }, [inputDevices, selectedDeviceId]);

  // ── Record ───────────────────────────────────────────────────────────────
  const busy =
    state.kind === 'preroll' ||
    state.kind === 'recording' ||
    state.kind === 'generating';

  const startRecording = useCallback(async () => {
    if (!hasVoice || atLimit || busy) return;
    if (!toolId) {
      setState({ kind: 'error', message: 'Session not ready — please reload.' });
      return;
    }

    interruptedRef.current = false;
    // Stop any take-preview / version playback that's still going so the
    // beeps + voice come through cleanly. Applies to all browsers — without
    // this, an active take-preview keeps playing through the countdown.
    stopAllPlayers();
    setPlayingTakeId(null);

    // ── 1. SYNCHRONOUSLY schedule the beeps (FB#25). ─────────────────────
    // playPreroll() must run BEFORE any await so the AudioContext is touched
    // while user-gesture activation is still live on Safari. The hook uses
    // the SHARED ctx (warm/running thanks to eagerInit on first pointerdown)
    // so the previously-flaky "create-then-async-resume" race is gone — see
    // comment on `playPreroll` in `useRecording.ts`.
    const PREROLL_INTERVAL_MS = 1000;
    const PREROLL_BEEPS = 3;
    let preroll: PrerollHandle;
    try {
      preroll = recordingRef.current.playPreroll({
        beeps: PREROLL_BEEPS,
        intervalMs: PREROLL_INTERVAL_MS,
      });
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Audio engine unavailable.';
      setState({ kind: 'error', message: msg });
      return;
    }
    // Snapshot endsAt AFTER the visible "go" moment fires — preroll.endsAt
    // starts as a sentinel and is only set to the real value once audio has
    // been scheduled (Safari resume can shift it forward).
    void preroll.go.then(() => {
      prerollEndsAtRef.current = preroll.endsAt;
    });
    void preroll.settle; // we don't await; ctx self-cleans

    setState({ kind: 'preroll' });

    // ── 2. Drive the visible countdown off wall-clock time. ──────────────
    // Tick at 50ms so the displayed digit changes within ~one frame of the
    // beep. Using clock-derived numbers (rather than a sequential setTimeout
    // chain) keeps the visual + audio in lockstep even if the JS event loop
    // is briefly stalled.
    const tickCountdown = () => {
      const remainingMs = preroll.endsAt - performance.now();
      if (remainingMs <= 0) {
        setCountdown(null);
        return;
      }
      const num = Math.max(
        1,
        Math.min(PREROLL_BEEPS, Math.ceil(remainingMs / PREROLL_INTERVAL_MS)),
      );
      setCountdown(num);
    };
    tickCountdown();
    const intervalId = window.setInterval(tickCountdown, 50);
    const cleanupCountdown = () => {
      window.clearInterval(intervalId);
      setCountdown(null);
    };

    // ── 3. Acquire mic permission. ───────────────────────────────────────
    // Already pre-prompted on overlay open (FB#26), but the user may have
    // picked a different device since — `acquire()` is a no-op if the cached
    // stream matches selectedDeviceId, otherwise it re-acquires.
    try {
      await recordingRef.current.acquire(selectedDeviceId || null);
    } catch (e: unknown) {
      cleanupCountdown();
      recordingRef.current.cancelPreroll();
      const msg =
        e instanceof Error ? e.message : 'Microphone access was denied.';
      setState({ kind: 'error', message: msg });
      return;
    }

    // ── 4. Start the recorder NOW (FB#27). ───────────────────────────────
    // Recording starts MID-countdown; the prefix is trimmed in stopRecording.
    // This guarantees we capture the very first phoneme of the user's "go".
    // MediaRecorder needs measurable startup time before its first
    // `dataavailable`, especially on Safari, and starting it AT the "go"
    // moment reliably drops the first ~0.3-0.5s of audio.
    try {
      recordingRef.current.beginRecording();
    } catch (e: unknown) {
      cleanupCountdown();
      recordingRef.current.cancelPreroll();
      const msg =
        e instanceof Error ? e.message : 'Recording could not start.';
      setState({ kind: 'error', message: msg });
      return;
    }

    // ── 5. Wait for the visible "go" moment, then transition. ────────────
    // Use the handle's `go` promise — endsAt may shift forward post-resume
    // on Safari; setTimeout(endsAt - perfNow) would race the actual schedule.
    await preroll.go;
    cleanupCountdown();
    // Kick the reference video at the visible "go" moment, in lockstep with
    // the talent starting to speak. .play() returns a promise that may
    // reject on autoplay-policy denial — silent-catch since the slider lets
    // the user mute the audio if it ever does play through unexpectedly,
    // and a video-less asset has `videoElRef.current === null`.
    const v = videoElRef.current;
    // Nicht starten, wenn das Mikro im Countdown weggefallen ist (Audit M24).
    if (v && stateRef.current.kind === 'preroll') {
      try { v.currentTime = 0; } catch { /* ignore */ }
      void v.play().catch(() => { /* autoplay denied — record audio still works */ });
    }
    // Guard: another close path may have stopped the recorder while we were
    // waiting. Only flip to 'recording' if we're still in 'preroll'.
    setState((prev) =>
      prev.kind === 'preroll' ? { kind: 'recording' } : prev,
    );
  }, [atLimit, busy, hasVoice, selectedDeviceId, toolId, videoElRef, setPlayingTakeId]);

  // Phase 6 (#173): stopRecording is now decoupled from STS generation. It
  // STOPS the recorder + trims the silent countdown prefix + stashes the
  // resulting blob as `loadedSource`. The user then clicks [Generate] to
  // actually call the STS API. This unlocks the multi-voice flow: same
  // recording → switch voice in picker → Generate again → second take in
  // the same session.
  const stopRecording = useCallback(async () => {
    if (state.kind !== 'recording') return;
    // Pause + rewind the reference video the moment the user clicks Stop.
    // No teardown / element-remount: same <video> stays mounted, just parked
    // back at frame 0 so the next [Record] starts from the beginning again.
    const vEl = videoElRef.current;
    if (vEl) {
      try { vEl.pause(); } catch { /* ignore */ }
      try { vEl.currentTime = 0; } catch { /* ignore */ }
    }
    // Use a transient 'generating' state for the trim window — short, but
    // visible feedback that the booth is finalising the capture. After the
    // trim resolves we drop back to 'idle' (the explicit Generate button
    // takes over from there).
    setState({ kind: 'generating' });
    try {
      const result = await recordingRef.current.stop();
      if (!result || !toolId) {
        setState({
          kind: 'error',
          message: interruptedRef.current
            ? MIC_DISCONNECTED_MESSAGE
            : 'Recording could not be finalised.',
        });
        return;
      }
      const { blob, mime, recordStartedAt } = result;

      // FB#27: trim the silent countdown prefix. We started the recorder as
      // soon as the mic stream was available (typically before the last beep),
      // so the captured blob has up to ~`prerollEndsAt - recordStartedAt`
      // seconds of pre-"go" audio. +50ms covers the microphone-on click
      // artefact most browsers introduce on the first MediaRecorder chunk.
      const prerollEndsAt = prerollEndsAtRef.current;
      const headSeconds =
        prerollEndsAt != null
          ? Math.max(0, (prerollEndsAt - recordStartedAt) / 1000) + 0.05
          : 0.05;

      // Best-effort trim. If decode fails (rare — exotic codec / corrupt
      // chunk), fall back to the original blob so the user at least gets
      // a take rather than a hard error.
      let processedBlob: Blob = blob;
      let processedMime: string = mime;
      try {
        processedBlob = await trimAudioHead(blob, headSeconds);
        processedMime = processedBlob.type || 'audio/wav';
      } catch (trimErr) {
        console.warn(
          '[sts] head-trim failed — falling back to untrimmed blob',
          trimErr,
        );
      }

      setLoadedSource({ blob: processedBlob, mime: processedMime, label: 'Recording' });
      // Claim a fresh session number for this new input — same voice +
      // Generate on a new recording now produces a new take slot rather
      // than overwriting the previous recording's slot (#173 follow-up).
      setSessionNum(beginStsRecordingSession(currentAssetIdx));
      setState({ kind: 'idle' });
    } catch (e: unknown) {
      const msg =
        e instanceof Error && e.message ? e.message : 'Recording could not be finalised.';
      setState({ kind: 'error', message: msg });
      toast.show(`Recording error: ${msg}`, 3500);
    }
  }, [state.kind, toast, toolId, beginStsRecordingSession, currentAssetIdx, videoElRef, setLoadedSource, setSessionNum]);
  stopRecordingRef.current = stopRecording;

  return {
    state,
    setState,
    busy,
    countdown,
    permState,
    inputDevices,
    selectedDeviceId,
    setSelectedDeviceId,
    requestMicPermission,
    startRecording,
    stopRecording,
    recordingRef,
  };
}
